import * as React from "react"
import { AlertTriangle } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { OPERATING_MODE_OPTIONS } from "@/types/alfred-config"
import {
  ADMIN_IDLE_OPTIONS,
  DISPLAY_TIME_OPTIONS,
  OFFLINE_BATCH_OPTIONS,
  SOUND_VALID_OPTIONS,
  SOUND_INVALID_OPTIONS,
  SOUND_WARNING_OPTIONS,
  TURNSTILE_MODE_OPTIONS,
  UNLOCKED_ACCESS_OPTIONS,
  VALIDATION_VENDOR_OPTIONS,
} from "@/components/alfred/alfred-device-form-fields"
import { AppleVendorEditor } from "@/components/alfred/apple-vendor-editor"
import { DisplayConfigurationEditor } from "@/components/alfred/display-configuration-editor"
import { GoogleVendorEditor } from "@/components/alfred/google-vendor-editor"
import { ProVenueInstanceEditor } from "@/components/alfred/pro-venue-instance-editor"
import { MaskedCredentialInput } from "@/components/shared/masked-credential-input"
import type {
  AppleVendor,
  DisplayConfigurationItem,
  GoogleVendor,
  ProVenueInstanceInput,
} from "@/types/alfred-config"
import type { BulkUpdateAlfredDeviceValue } from "@/types/alfred-device"

type BulkCollectionKey =
  | "proVenueInstances"
  | "displayConfiguration"
  | "appleVendors"
  | "googleVendors"

type BulkPropertyDescriptor =
  | { key: string; label: string; type: "string" }
  | { key: string; label: string; type: "masked-credential" }
  | { key: string; label: string; type: "number"; min?: number }
  | { key: string; label: string; type: "boolean" }
  | { key: BulkCollectionKey; label: string; type: "collection"; itemNoun: string }
  | {
      key: string
      label: string
      type: "enum"
      options: ReadonlyArray<{ value: string | number; label: string }>
      valueType?: "number"
    }

export const BULK_PROPERTIES: BulkPropertyDescriptor[] = [
  { key: "adminModeIdleWindow",        label: "Admin Mode Idle Window",         type: "enum", options: ADMIN_IDLE_OPTIONS, valueType: "number" },
  { key: "appleVendors",               label: "Apple Vendors",                  type: "collection", itemNoun: "credential" },
  { key: "areaCode",                   label: "Area Code",                      type: "string" },
  { key: "deviceCode",                 label: "Device Code",                    type: "string" },
  { key: "displayConfiguration",        label: "Display Configuration",          type: "collection", itemNoun: "screen" },
  { key: "enableFan",                  label: "Enable Fan",                     type: "boolean" },
  { key: "enableHotspot",              label: "Enable Hotspot",                 type: "boolean" },
  { key: "enableManualValidationMask", label: "Enable Manual Validation Mask",  type: "boolean" },
  { key: "enableScanReset",            label: "Enable Scan Reset",              type: "boolean" },
  { key: "exitScanningAllowed",        label: "Exit Scanning Allowed",          type: "boolean" },
  { key: "facilityCode",               label: "Facility Code",                  type: "string" },
  { key: "googleVendors",              label: "Google Vendors",                 type: "collection", itemNoun: "credential" },
  { key: "idleStateColor",             label: "Idle State Color",               type: "string" },
  { key: "invalidDisplayTime",         label: "Invalid Display Time",           type: "enum", options: DISPLAY_TIME_OPTIONS, valueType: "number" },
  { key: "isLockFailSafe",             label: "Lock Fail Safe",                 type: "boolean" },
  { key: "league",                     label: "League",                         type: "string" },
  { key: "mainBackgroundColor",        label: "Main Background Color",          type: "string" },
  { key: "maxCpuTemperature",          label: "Max CPU Temperature",            type: "number" },
  { key: "minCpuTemperature",          label: "Min CPU Temperature",            type: "number" },
  { key: "nfcEnabled",                 label: "NFC Enabled",                    type: "boolean" },
  { key: "offlineBatchUploadLimit",    label: "Offline Batch Upload Limit",     type: "enum", options: OFFLINE_BATCH_OPTIONS, valueType: "number" },
  { key: "offlineValidationMode",      label: "Offline Validation Mode",        type: "boolean" },
  { key: "operatingMode",              label: "Operating Mode",                 type: "enum", options: OPERATING_MODE_OPTIONS, valueType: "number" },
  { key: "proVenueInstances",          label: "ProVenue Instances",             type: "collection", itemNoun: "instance" },
  { key: "proVenueUser",               label: "ProVenue User",                  type: "string" },
  { key: "secondaryBackgroundColor",   label: "Secondary Background Color",     type: "string" },
  { key: "soundInvalidNumber",         label: "Sound Invalid Number",           type: "enum", options: SOUND_INVALID_OPTIONS, valueType: "number" },
  { key: "soundValidNumber",           label: "Sound Valid Number",             type: "enum", options: SOUND_VALID_OPTIONS, valueType: "number" },
  { key: "soundWarningNumber",         label: "Sound Warning Number",           type: "enum", options: SOUND_WARNING_OPTIONS, valueType: "number" },
  { key: "ticketmasterConsumerKey",    label: "Ticketmaster Consumer Key",      type: "masked-credential" },
  { key: "ticketmasterVenueId",        label: "Ticketmaster Venue ID",          type: "string" },
  { key: "turnstileMode",              label: "Turnstile Mode",                 type: "enum", options: TURNSTILE_MODE_OPTIONS, valueType: "number" },
  { key: "unlockedAccessTime",         label: "Unlocked Access Time",           type: "enum", options: UNLOCKED_ACCESS_OPTIONS, valueType: "number" },
  { key: "useSimpleResponse",          label: "Use Simple Response",            type: "boolean" },
  { key: "validationVendor",           label: "Validation Vendor",              type: "enum", options: VALIDATION_VENDOR_OPTIONS },
  { key: "validDisplayTime",           label: "Valid Display Time",             type: "enum", options: DISPLAY_TIME_OPTIONS, valueType: "number" },
  { key: "wakeLockEnabled",            label: "Wake Lock Enabled",              type: "boolean" },
]

export type BulkValue = BulkUpdateAlfredDeviceValue

interface BulkUpdateBarProps {
  selectedCount: number
  onSave: (property: string, value: BulkValue) => void | Promise<void>
  onCancel: () => void
  isSaving?: boolean
}

export function BulkUpdateBar({
  selectedCount,
  onSave,
  onCancel,
  isSaving = false,
}: BulkUpdateBarProps) {
  const [property, setProperty] = React.useState("")
  const [value, setValue] = React.useState<BulkValue | undefined>()
  const [useGlobalDefault, setUseGlobalDefault] = React.useState(false)
  const isMutating = isSaving

  const descriptor = BULK_PROPERTIES.find((p) => p.key === property)

  const handlePropertyChange = (key: string) => {
    setProperty(key)
    setValue(undefined)
    setUseGlobalDefault(false)
  }

  const hasValue =
    value !== "" &&
    value !== undefined &&
    (typeof value !== "number" || Number.isFinite(value))
  const canSave = selectedCount > 0 && property !== "" && (useGlobalDefault || hasValue)

  const handleSave = () => {
    if (!canSave) return
    if (useGlobalDefault) {
      void onSave(property, null)
      return
    }
    if (
      value === undefined ||
      value === "" ||
      value === null ||
      (typeof value === "number" && !Number.isFinite(value))
    ) return
    void onSave(property, value)
  }

  return (
    <div
      role="region"
      aria-label="Bulk configuration editor"
      data-tour="bulk-editor"
      className="flex flex-wrap items-center gap-3"
    >
      <span
        aria-live="polite"
        className="rounded-md bg-primary/10 px-2.5 py-1 text-sm font-medium text-foreground"
      >
        {selectedCount === 0
          ? "No devices selected"
          : `${selectedCount} device${selectedCount === 1 ? "" : "s"} selected`}
      </span>

      <div className="h-5 w-px bg-border" />

      {/* property selector */}
      <Select
        value={property}
        onValueChange={handlePropertyChange}
        disabled={isMutating}
      >
        <SelectTrigger className="w-56 h-9 bg-background border-border text-sm">
          <SelectValue placeholder="Select property..." />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectGroup>
            {BULK_PROPERTIES.map((p) => (
              <SelectItem key={p.key} value={p.key}>
                {p.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      {descriptor && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="bulk-use-global-default"
            checked={useGlobalDefault}
            onCheckedChange={(checked) => setUseGlobalDefault(checked === true)}
            disabled={isMutating}
          />
          <Label htmlFor="bulk-use-global-default">Use global default</Label>
        </div>
      )}

      {/* value input — rendered based on property type */}
      {descriptor && !useGlobalDefault && (
        <>
          {descriptor.type === "string" && (
            <Input
              value={(value as string | undefined) ?? ""}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter value..."
              disabled={isMutating}
              className="w-48 h-9 bg-background border-border text-sm"
            />
          )}

          {descriptor.type === "masked-credential" && (
            <MaskedCredentialInput
              value={(value as string | undefined) ?? ""}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Enter replacement value..."
              disabled={isMutating}
              className="w-48 h-9 bg-background border-border text-sm"
            />
          )}

          {descriptor.type === "number" && (
            <Input
              type="number"
              min={descriptor.min}
              value={(value as number | undefined) ?? ""}
              disabled={isMutating}
              onChange={(e) => {
                const nextValue = e.target.valueAsNumber
                setValue(Number.isFinite(nextValue) ? nextValue : undefined)
              }}
              placeholder="Enter value..."
              className="w-36 h-9 bg-background border-border text-sm"
            />
          )}

          {descriptor.type === "boolean" && (
            <Select
              value={value === undefined ? "" : String(value)}
              onValueChange={(v) => setValue(v === "true")}
              disabled={isMutating}
            >
              <SelectTrigger className="w-36 h-9 bg-background border-border text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="true">Enabled</SelectItem>
                  <SelectItem value="false">Disabled</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          )}

          {descriptor.type === "enum" && (
            <Select
              value={value === undefined ? "" : String(value)}
              disabled={isMutating}
              onValueChange={(selectedValue) => {
                const option = descriptor.options.find((item) => String(item.value) === selectedValue)
                if (option) {
                  setValue(descriptor.valueType === "number" ? Number(option.value) : option.value)
                }
              }}
            >
              <SelectTrigger className="w-48 h-9 bg-background border-border text-sm">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {descriptor.options.map((o) => (
                    <SelectItem key={String(o.value)} value={String(o.value)}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}

          {descriptor.type === "collection" && (
            <div className="flex items-center gap-3">
              <BulkCollectionEditor
                property={descriptor.key}
                value={Array.isArray(value) ? value : []}
                onChange={setValue}
                disabled={isMutating}
              />
              <span className="text-sm text-muted-foreground">
                {Array.isArray(value)
                  ? `${value.length} ${descriptor.itemNoun}${value.length === 1 ? "" : "s"} staged`
                  : "Open Manage and click Done to stage the replacement list"}
              </span>
            </div>
          )}
        </>
      )}

      {descriptor?.type === "collection" && (
        <Alert className="basis-full">
          <AlertTriangle />
          <AlertTitle>
            {useGlobalDefault ? "Global list inheritance" : "Complete list replacement"}
          </AlertTitle>
          <AlertDescription>
            {useGlobalDefault
              ? "Saving sends null and removes the device-level entries on every selected device."
              : "Saving replaces this entire list on every selected device. An empty staged list removes the device-level entries, so those devices inherit the global list."}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex-1" />

      {/* actions */}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onCancel}
        disabled={isMutating}
      >
        Cancel
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={!canSave || isMutating}
        onClick={handleSave}
      >
        {isSaving ? "Saving..." : "Save"}
      </Button>
    </div>
  )
}

interface BulkCollectionEditorProps {
  property: BulkCollectionKey
  value: unknown[]
  onChange: (next: BulkValue) => void
  disabled?: boolean
}

function BulkCollectionEditor({
  property,
  value,
  onChange,
  disabled = false,
}: BulkCollectionEditorProps) {
  return (
    <fieldset disabled={disabled} className="contents">
      <BulkCollectionEditorContent
        property={property}
        value={value}
        onChange={onChange}
      />
    </fieldset>
  )
}

function BulkCollectionEditorContent({ property, value, onChange }: BulkCollectionEditorProps) {
  switch (property) {
    case "proVenueInstances":
      return (
        <ProVenueInstanceEditor
          items={value as ProVenueInstanceInput[]}
          onChange={onChange}
          requireSecret
        />
      )
    case "displayConfiguration":
      return (
        <DisplayConfigurationEditor
          items={value as DisplayConfigurationItem[]}
          onChange={onChange}
        />
      )
    case "appleVendors":
      return (
        <AppleVendorEditor
          items={value as AppleVendor[]}
          onChange={onChange}
          requireSecret
        />
      )
    case "googleVendors":
      return (
        <GoogleVendorEditor
          items={value as GoogleVendor[]}
          onChange={onChange}
          requireSecret
        />
      )
  }
}
